import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectAllOptions } from "../../customisation-slice";
import Button from "../button";
import SidebarActionButtons from "../sidebar-action-buttons";
import { useWatchViewContext } from "../../providers/watch-view-provider";
import { useSidebarAnimationContext } from "../../providers/sidebar-animation-provider";
import { getAsset } from "../../support/asset-import";
import { CUSTOMISATION_OPTIONS } from "../../constants/all-customisation-options";
import ConfigCell from "./config-cell";
import { useDialogContext } from "../../providers/dialog-provider";
import { useProductVariantContext } from "../../providers/product-variant-provider";
import { getPriceFromVariant } from "../../support/helpers";
import { addProductToCart } from "../../services/shopify";
import { createWatchImageUrl } from "../../services/firebase";
import { DialogTypes } from "../../constants/dialog-types";

const SummaryContainer = () => {
  const [finalConfiguration, setFinalConfiguration] = useState([]);
  const options = useSelector(selectAllOptions);
  const { goToSpecificView } = useWatchViewContext();
  const { openDialog, closeDialog } = useDialogContext();
  const { resetCustomisationStateWithAnimation, updateEditStateWithAnimation } =
    useSidebarAnimationContext();
  const { getCurrentProductVariant } = useProductVariantContext();

  const startOver = () => {
    resetCustomisationStateWithAnimation();
    goToSpecificView(0);
    closeDialog();
  };

  const getCurrentPrice = () => {
    const currentVariant = getCurrentProductVariant();
    return getPriceFromVariant(currentVariant);
  };

  useEffect(() => {
    const finalConfig = Object.keys(options).map((key) => {
      const optionCustomisationState = options[key];
      const optionDetail = CUSTOMISATION_OPTIONS[key];
      const selectedOptionDetail =
        optionDetail.options[optionCustomisationState.selected];
      return {
        key: key,
        title: optionDetail.title,
        image: selectedOptionDetail?.watchLayerThumbnail,
        selectionTitle: selectedOptionDetail?.title,
        selectionText: optionCustomisationState.text,
        selectionKey: optionCustomisationState.selected,
      };
    });
    setFinalConfiguration(finalConfig);
    goToSpecificView(0);
  }, [options]);

  const addToCart = async () => {
    openDialog({
      type: DialogTypes.LOADING,
      loadingText: "Adding to your basket...",
    });
    const currentVariant = getCurrentProductVariant();
    try {
      const imageUrl = await createWatchImageUrl(finalConfiguration);
      await addProductToCart(currentVariant, finalConfiguration, imageUrl);
      window.location.href = `${window.location.origin}/cart`;
    } catch (error) {
      console.log(error);
    }
  };

  const actionButtons = [
    <Button
      key="Add to Basket"
      label="Add to Basket"
      onClick={() => addToCart()}
      icon="Cart"
    />,
    <Button
      key="Start Over"
      label="Start Over"
      onClick={() =>
        openDialog({
          type: DialogTypes.CONFIRMATION,
          title: "Start Over?",
          description:
            "All of your customisations will be removed. Are you sure you want to start over?",
          onSubmit: startOver,
          onClose: closeDialog,
        })
      }
      outline={true}
      icon="Restart"
    />,
  ];

  return (
    <>
      <div className="p-8 border-b border-light-grey">
        <img src={getAsset("BamfordLogo")} alt="logo" />
        <div className="heading-1 mt-8">The Bamford GMT</div>
        <div className="heading-3">{`${getCurrentPrice()} inc VAT`}</div>
        <div className="body-1 mt-4">
          Displaying Bamford London’s own unique colourways and housing the
          Swiss Made self-winding Sellita SW330-2 movement with 25 jewels and a
          42 hour power reserve.
        </div>
      </div>
      <div className="flex-grow p-8 md:overflow-y-auto mb-160 md:mb-0">
        <div className="heading-4">Your configuration</div>
        {finalConfiguration.map((config) => (
          <ConfigCell
            key={config.key}
            title={config.title}
            image={config.image}
            optionKey={config.key}
            selectionText={config.selectionText}
            selectionTitle={config.selectionTitle}
            onClick={() => updateEditStateWithAnimation(config.key)}
          />
        ))}
      </div>
      <div className="fixed bottom-0 md:relative bg-white w-full">
        <SidebarActionButtons buttons={actionButtons} />
      </div>
    </>
  );
};

export default SummaryContainer;
